/**
 * Aug 2026 · Invoice Numbering Settings — Domestic + Export GST
 *
 * Admin-only panel that toggles Auto vs Manual invoice numbering per
 * kind. When Auto is ON, the admin can pin a custom prefix (e.g.
 * `INV-26-27-`) and starting sequence. Starting Sequence is a
 * MINIMUM NEXT-NUMBER FLOOR — see A1 · R-3B below.
 *
 * When Auto is OFF, the user must key the invoice_no on Draft Save.
 *
 * A1 · PMS.V2.8 · P1 (Feb 2026) — Owner-authorised corrections
 * ------------------------------------------------------------------
 * R-2  Hint text for the Export kind no longer advertises a legacy
 *      `ECI-YY-YY-#####` fallback. Backend fails closed on blank
 *      prefix (see `exports/eci.py::_next_eci_doc_no`); this panel's
 *      wording now matches.
 *
 * R-3  "Next number" preview is sourced from the SAME backend algorithm
 *      that will actually allocate the next invoice number. We call
 *      `GET /settings/invoice-numbering/preview` after mount and after
 *      every successful save.
 *
 * R-3B Owner-authoritative allocation algorithm (frozen 2026-10-07):
 *      next = MAX(configured_starting_sequence, existing_max + 1)
 *      Starting Sequence is a FLOOR, not a seed. It stays in force
 *      even when existing invoices are present. Examples:
 *         start=201, max=103 → 201
 *         start=201, max=201 → 202
 *         start=201, max=250 → 251
 *         start=201, no series → 201
 *
 * See `/app/memory/A1_ECI_Invoice_Numbering_Configuration_Regression.md`
 * for the full A1 Problem Resolution & Freeze Record.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FloppyDiskIcon, HashIcon } from "@phosphor-icons/react";

const inp = "w-full h-9 px-2.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";

// Example-prefix placeholders only. Export placeholder uses `EXP-` to
// visually suggest the Owner-approved shape (`EXP-26-27/E/`); the UI
// no longer uses this constant to synthesize a Next-number preview
// (that comes from the backend preview endpoint, A1 R-3).
const DEFAULT_ROOT = { gst: "INV", export_gst: "EXP" };

const KINDS = [
  { key: "gst",        label: "Domestic GST Invoice",
    hint: "Auto-generation uses the configured prefix. If the prefix is blank, the system falls back to `INV-YY-YY-#####` (current Domestic policy — subject to a separate Owner business-rule review)." },
  // A1 · R-2 — wording matches the fail-closed backend. No fallback
  // for Export; invoice creation is BLOCKED when prefix is blank.
  { key: "export_gst", label: "Export Invoice (ECI / EGSTI)",
    hint: "Auto-generation requires a configured Export Invoice prefix. If the prefix is blank, Export Commercial Invoice creation is blocked. ECI owns this sequence; EGSTI inherits the ECI number verbatim." },
];

export default function InvoiceNumberingPanel() {
  const [cfg, setCfg] = useState({ gst: null, export_gst: null });
  const [preview, setPreview] = useState({ gst: null, export_gst: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadPreview = async () => {
    try {
      const { data } = await api.get("/settings/invoice-numbering/preview");
      setPreview(data || {});
    } catch {
      // Non-fatal — the config panel still renders with a muted
      // "preview unavailable" line (see render below).
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/invoice-numbering");
        setCfg(data || {});
      } catch { toast.error("Failed to load numbering settings"); }
      await loadPreview();
      setLoading(false);
    })();
  }, []);

  const setField = (kind, field) => (val) => {
    setCfg(prev => ({
      ...prev,
      [kind]: { ...(prev[kind] || {}), [field]: val },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const { key } of KINDS) {
        const block = cfg[key] || {};
        payload[key] = {
          auto_generation: !!block.auto_generation,
          prefix: (block.prefix || "").trim(),
          starting_sequence: Math.max(1, parseInt(block.starting_sequence || 1, 10)),
        };
      }
      const { data } = await api.put("/settings/invoice-numbering", payload);
      setCfg(data || cfg);
      await loadPreview();
      toast.success("Invoice numbering settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-zinc-500">Loading numbering settings…</div>;

  return (
    <div className="space-y-4" data-testid="invoice-numbering-panel">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline">Admin</div>
          <h2 className="font-display text-2xl font-bold text-white">Invoice Numbering</h2>
          <div className="text-xs text-zinc-500 mt-1">Toggle Auto vs Manual entry per invoice type. Duplicate numbers are rejected across both types.</div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="numbering-save"
          className="h-10 px-4 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
        >
          <FloppyDiskIcon size={14} weight="bold" /> {saving ? "Saving…" : "Save Numbering"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {KINDS.map(({ key, label, hint }) => {
          const c = cfg[key] || { auto_generation: true, prefix: "", starting_sequence: 1 };
          const auto = !!c.auto_generation;
          const pv = preview[key] || null;
          return (
            <div key={key} className="tactical-card p-5 space-y-3" data-testid={`numbering-card-${key}`}>
              <div className="flex items-center gap-2">
                <HashIcon size={16} className="text-[var(--accent)]" />
                <div className="overline">{label}</div>
              </div>

              {/* Auto toggle */}
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`numbering-auto-${key}`}>
                  <input
                    type="checkbox"
                    checked={auto}
                    onChange={e => setField(key, "auto_generation")(e.target.checked)}
                    className="w-4 h-4 accent-[var(--accent)]"
                  />
                  <span className="text-sm text-white">Auto-Generation</span>
                </label>
                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm ${auto ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/5" : "text-amber-300 border-amber-500/40 bg-amber-500/5"}`}>
                  {auto ? "ON" : "OFF · manual entry required"}
                </span>
              </div>

              {/* Prefix + Starting Sequence — only meaningful when auto is ON */}
              <div className={`grid grid-cols-2 gap-3 ${auto ? "" : "opacity-50 pointer-events-none"}`}>
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1 block">Prefix</label>
                  <input
                    data-testid={`numbering-prefix-${key}`}
                    value={c.prefix || ""}
                    onChange={e => setField(key, "prefix")(e.target.value)}
                    placeholder={`${DEFAULT_ROOT[key]}-26-27-`}
                    className={`${inp} font-mono`}
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1 block">Starting Sequence</label>
                  <input
                    type="number" min="1"
                    data-testid={`numbering-seq-${key}`}
                    value={c.starting_sequence ?? 1}
                    onChange={e => setField(key, "starting_sequence")(e.target.value)}
                    className={`${inp} font-mono tabular-nums`}
                  />
                </div>
              </div>

              {/* A1 · R-3 / R-3B — Next-number preview sourced from backend */}
              <div className="text-[11px] text-zinc-500 leading-relaxed space-y-1" data-testid={`numbering-preview-${key}`}>
                {pv ? (
                  pv.status === "ok" ? (
                    <>
                      Next number: <span className="font-mono text-[var(--accent)]" data-testid={`numbering-next-${key}`}>{pv.next_no}</span>
                      <span className="text-zinc-600 ml-2">
                        ({pv.algorithm === "existing-series-max+1"
                            ? `existing series max ${String(pv.existing_max).padStart(5, "0")} + 1`
                            : pv.algorithm === "starting-sequence-floor-above-max"
                              ? `starting-sequence floor ${String(pv.starting_sequence).padStart(5, "0")} ≥ existing max ${String(pv.existing_max).padStart(5, "0")} + 1`
                              : `starting-sequence floor (no existing records for this prefix)`})
                      </span>
                    </>
                  ) : pv.status === "manual" ? (
                    <>
                      <span className="text-amber-300 font-mono uppercase tracking-widest">Manual entry required</span>
                      <span className="text-zinc-600 ml-2">{pv.reason}</span>
                    </>
                  ) : pv.status === "blocked" ? (
                    <>
                      <span className="text-rose-300 font-mono uppercase tracking-widest" data-testid={`numbering-blocked-${key}`}>Blocked</span>
                      <span className="text-zinc-600 ml-2">{pv.reason}</span>
                    </>
                  ) : (
                    <span className="text-zinc-600">Preview unavailable.</span>
                  )
                ) : (
                  <span className="text-zinc-600">Preview unavailable (save pending or endpoint unreachable).</span>
                )}
                <div className="text-zinc-600">
                  Starting Sequence is a minimum next-number FLOOR — it stays in force even when existing invoices are present. The system allocates <span className="font-mono">MAX(starting&nbsp;sequence, existing&nbsp;max&nbsp;+&nbsp;1)</span>.
                </div>
                <div className="mt-1 text-zinc-600">{hint}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
